import { z } from "zod";
import { Id } from "../common/ids.js";
import { Money } from "../common/money.js";
import { PageQuery, page } from "../common/pagination.js";
import { Quantity } from "../common/quantity.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
import { ContentMessage } from "../messaging/content-access.routes.js";
import { TradeSide } from "../trade/terms.js";
/**
 * After-sales cases (AFS-1 to AFS-4, cards 087 to 091; `docs/02-domain.md`, "After-sales and disputes"). The buyer
 * opens a case on a paid order within the after-sales window; while it is open the order's confirmation stops.
 */
export const AftersaleCaseType = z
    .enum(["refund", "return", "replacement", "partial_refund"])
    .meta({ id: "AftersaleCaseType" });
export const AftersaleCaseStatus = z
    .enum([
    "requested",
    "seller_responding",
    "agreed",
    "executing",
    "escalated",
    "under_review",
    "resolved",
    "closed",
    "withdrawn",
])
    .meta({ id: "AftersaleCaseStatus" });
export const AftersaleCaseLine = z
    .object({ orderLineId: Id, quantity: Quantity })
    .meta({ id: "AftersaleCaseLine" });
export const AftersaleEvidence = z
    .object({
    id: Id,
    side: TradeSide,
    kind: z.enum(["file", "note", "conversation_export"]),
    fileId: Id.nullable().describe("Read through the files API by either party"),
    note: z.string().nullable(),
    messages: z
        .array(ContentMessage)
        .nullable()
        .describe("For a conversation export: the messages a party picked, as they read when picked"),
    sha256: z
        .string()
        .nullable()
        .describe("For a conversation export: the SHA-256 of the messages as exported, in their JSON form"),
    late: z.boolean().describe("Added after the evidence window closed"),
    at: Timestamp,
})
    .meta({ id: "AftersaleEvidence" });
export const AftersaleCaseAction = z
    .enum([
    "respond",
    "accept_counter",
    "ship_return",
    "confirm_return",
    "record_settlement",
    "confirm_settlement",
    "add_evidence",
    "escalate",
    "appeal",
    "withdraw",
])
    .meta({ id: "AftersaleCaseAction" });
/** A resolver's decision (AFS-3, card 089): grant what was asked, a lower amount, or reject, always with reasons. */
export const AftersaleDecision = z
    .object({
    outcome: z.enum(["grant", "partial", "reject"]),
    amount: Money.nullable(),
    reasons: z.string(),
    appeal: z.boolean().describe("The final decision on an appeal"),
    decidedAt: Timestamp,
})
    .meta({ id: "AftersaleDecision" });
export const AftersaleCaseStep = z
    .enum([
    "seller_answer",
    "buyer_reply",
    "return_shipment",
    "return_arrival",
    "settlement",
    "settlement_confirmation",
    "dispute",
    "none",
])
    .meta({ id: "AftersaleCaseStep" });
/** How an agreed outcome was carried out between the parties (card 088): the refund paid or the replacement sent. */
export const AftersaleSettlement = z
    .object({
    kind: z.enum(["refund", "replacement"]),
    amount: Money.nullable(),
    paidOn: z.iso.date().nullable(),
    reference: z.string().nullable().describe("The bank's reference for the refund"),
    proofFileId: Id.nullable(),
    carrier: z.string().nullable(),
    trackingNumber: z.string().nullable(),
    shippedAt: Timestamp.nullable(),
    status: z.enum(["recorded", "confirmed", "not_received"]),
    confirmedBy: z.enum(["buyer", "timeout"]).nullable(),
    note: z.string().nullable().describe("The buyer's word when it did not arrive"),
    recordedAt: Timestamp,
    answeredAt: Timestamp.nullable(),
})
    .meta({ id: "AftersaleSettlement" });
export const AftersaleCaseSummary = z
    .object({
    id: Id,
    orderId: Id,
    orderNumber: z.string(),
    side: TradeSide.describe("The caller's side"),
    type: AftersaleCaseType,
    status: AftersaleCaseStatus,
    requested: Money.nullable().describe("What the buyer asks for; none for a replacement"),
    openedAt: Timestamp,
    nextStep: AftersaleCaseStep,
    waitingOn: z.enum(["buyer", "seller", "platform"]).nullable(),
    responseDueAt: Timestamp.nullable().describe("When the side the case waits on must have acted: the seller's silence escalates the case, the buyer's lapses it after a counter or rejection, and confirms a recorded settlement"),
})
    .meta({ id: "AftersaleCaseSummary" });
export const AftersaleCaseView = AftersaleCaseSummary.extend({
    reason: z.string(),
    lines: z.array(AftersaleCaseLine),
    paid: Money.describe("What the buyer paid on the order, the most any refund can be"),
    settlement: z
        .enum(["between_parties", "platform"])
        .describe("`between_parties` on an order paid by bank transfer: the platform holds no money, so a refund is paid by the seller and confirmed by the buyer (ADR 0005)"),
    answer: z.enum(["accepted", "countered", "rejected"]).nullable(),
    answerNote: z.string().nullable(),
    counter: Money.nullable(),
    agreed: Money.nullable(),
    returnShipment: z
        .object({
        carrier: z.string(),
        trackingNumber: z.string(),
        shippedAt: Timestamp,
        receivedAt: Timestamp.nullable(),
    })
        .nullable(),
    settlementRecord: AftersaleSettlement.nullable().describe("The latest settlement recorded"),
    decision: AftersaleDecision.nullable().describe("The latest decision; the appeal's when there was one"),
    appeal: z
        .object({
        side: TradeSide,
        reason: z.string(),
        filedAt: Timestamp,
        status: z.enum(["filed", "resolved"]),
    })
        .nullable(),
    escalatedAt: Timestamp.nullable(),
    evidenceDueAt: Timestamp.nullable(),
    appealDueAt: Timestamp.nullable(),
    closedAt: Timestamp.nullable(),
    version: z.number().int().positive(),
    evidence: z.array(AftersaleEvidence),
    timeline: z.array(z.object({
        at: Timestamp,
        actor: z.enum(["buyer", "seller", "platform"]),
        action: z.string(),
    })),
    actions: z.array(AftersaleCaseAction).describe("What the caller may do now"),
}).meta({ id: "AftersaleCaseView" });
const Party = { surface: "tenant", auth: "user" };
const CaseId = z.object({ caseId: Id });
const EvidenceFiles = z
    .array(Id)
    .max(10)
    .default([])
    .describe("Clean `evidence` files uploaded for the order");
export const openAftersaleCase = defineRoute({
    ...Party,
    permission: "aftersale:open",
    id: "aftersales.cases.open",
    method: "POST",
    path: "/v1/orders/{orderId}/aftersale-cases",
    marks: ["tenant", "idem"],
    summary: "The buyer opens a case on a paid order, from the first payment until the after-sales window ends after delivery: refund, return, replacement, or partial refund, with lines and quantities for a return or replacement, an amount within what was paid, a reason, and evidence. One case is open per order at a time; while it is open, the order's confirmation stops",
    prd: ["AFS-1", "AFS-4"],
    request: {
        params: z.object({ orderId: Id }),
        body: z.object({
            type: AftersaleCaseType,
            lines: z.array(AftersaleCaseLine).max(50).default([]),
            amount: Money.optional().describe("Required for a partial refund and a return; a refund defaults to everything paid; none for a replacement"),
            reason: z.string().trim().min(10).max(2000),
            evidence: EvidenceFiles,
        }),
    },
    responses: { 201: { description: "The case", schema: AftersaleCaseView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "WINDOW_CLOSED",
        "CASE_OPEN",
    ],
});
export const listAftersaleCases = defineRoute({
    ...Party,
    permission: "order:read",
    id: "aftersales.cases.list",
    method: "GET",
    path: "/v1/aftersale-cases",
    marks: ["tenant"],
    summary: "The enterprise's cases as buyer, seller, or both, newest first: open or ended, one order's, or those waiting for the caller's side (`waiting=you`)",
    prd: ["AFS-1", "WEB-5"],
    request: {
        query: PageQuery.extend({
            side: TradeSide.optional(),
            state: z.enum(["open", "ended"]).optional(),
            orderId: Id.optional(),
            waiting: z.enum(["you"]).optional(),
        }),
    },
    responses: { 200: { description: "Cases", schema: page(AftersaleCaseSummary) } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const countAftersaleCases = defineRoute({
    ...Party,
    permission: "order:read",
    id: "aftersales.cases.counts",
    method: "GET",
    path: "/v1/aftersale-cases/counts",
    marks: ["tenant"],
    summary: "On one side, the cases open and those waiting for that side, for the home screen (WEB-5, card 091)",
    prd: ["WEB-5"],
    request: { query: z.object({ side: TradeSide }) },
    responses: {
        200: {
            description: "Counts",
            schema: z.object({
                open: z.number().int().nonnegative(),
                waitingForYou: z.number().int().nonnegative(),
            }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "VALIDATION_FAILED"],
});
export const getAftersaleCase = defineRoute({
    ...Party,
    permission: "order:read",
    id: "aftersales.cases.get",
    method: "GET",
    path: "/v1/aftersale-cases/{caseId}",
    marks: ["tenant"],
    summary: "A case with its lines, amounts, windows, evidence from both sides, timeline, and what the caller may do",
    prd: ["AFS-1"],
    request: { params: CaseId },
    responses: { 200: { description: "The case", schema: AftersaleCaseView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const addAftersaleEvidence = defineRoute({
    ...Party,
    permission: "order:read",
    id: "aftersales.cases.evidence",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/evidence",
    marks: ["tenant", "idem"],
    summary: "Either side adds files or a note to an open case: the buyer's people with `aftersale:open`, the seller's with `aftersale:respond`. Evidence added after the evidence window is kept and marked late",
    prd: ["AFS-1", "AFS-3"],
    request: {
        params: CaseId,
        body: z.object({
            files: EvidenceFiles,
            note: z.string().trim().min(1).max(2000).optional(),
            messageIds: z
                .array(Id)
                .max(50)
                .default([])
                .describe("Entries picked from the order's conversation's log, from `conversation-messages`"),
        }),
    },
    responses: { 201: { description: "The case", schema: AftersaleCaseView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const withdrawAftersaleCase = defineRoute({
    ...Party,
    permission: "aftersale:open",
    id: "aftersales.cases.withdraw",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/withdraw",
    marks: ["tenant", "idem"],
    summary: "The buyer withdraws a case not yet agreed or decided; the order's confirmation resumes with the time it had left",
    prd: ["AFS-1"],
    request: { params: CaseId },
    responses: { 200: { description: "The case", schema: AftersaleCaseView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
const Note = z.string().trim().min(10).max(2000);
export const listAftersaleConversationMessages = defineRoute({
    ...Party,
    permission: "order:read",
    id: "aftersales.cases.conversation-messages",
    method: "GET",
    path: "/v1/aftersale-cases/{caseId}/conversation-messages",
    marks: ["tenant"],
    summary: "The order's conversation, newest first, for a person in it to pick messages as evidence (card 089); read from its log (card 159), files by their names only",
    prd: ["AFS-3"],
    request: {
        params: CaseId,
        query: z.object({ page: z.coerce.number().int().min(1).max(1000).default(1) }),
    },
    responses: {
        200: {
            description: "One page of messages",
            schema: z.object({ items: z.array(ContentMessage), nextPage: z.number().int().nullable() }),
        },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND"],
});
export const escalateAftersaleCase = defineRoute({
    ...Party,
    permission: "aftersale:escalate",
    id: "aftersales.cases.escalate",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/escalate",
    marks: ["tenant", "idem"],
    summary: "Takes a case to the platform, with a reason kept as the side's evidence: the buyer after the seller's counter-offer or rejection, or while an agreed outcome waits on the seller past its time; the seller when the buyer says a recorded settlement did not arrive. Both sides then have the RuleSet's evidence days",
    prd: ["AFS-3"],
    request: { params: CaseId, body: z.object({ reason: Note }) },
    responses: { 200: { description: "The case", schema: AftersaleCaseView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const appealAftersaleDecision = defineRoute({
    ...Party,
    permission: "aftersale:escalate",
    id: "aftersales.cases.appeal",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/appeal",
    marks: ["tenant", "idem"],
    summary: "Either side appeals a resolver's decision once, within the RuleSet's appeal days; another resolver hears it, and that decision is final",
    prd: ["AFS-3"],
    request: { params: CaseId, body: z.object({ reason: Note }) },
    responses: { 200: { description: "The case", schema: AftersaleCaseView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
        "WINDOW_CLOSED",
    ],
});
export const respondAftersaleCase = defineRoute({
    ...Party,
    permission: "aftersale:respond",
    id: "aftersales.cases.respond",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/respond",
    marks: ["tenant", "idem"],
    summary: "The seller answers a case: accepts what was asked, which goes on to its execution; counters with a lower amount (not for a replacement); or rejects, with a note. After a counter or rejection the buyer has the RuleSet's response days to accept, escalate, or withdraw, or the case lapses",
    prd: ["AFS-1"],
    request: {
        params: CaseId,
        body: z.object({
            answer: z.enum(["accept", "counter", "reject"]),
            amount: Money.optional(),
            note: Note.optional().describe("Required for a counter and a rejection"),
        }),
    },
    responses: { 200: { description: "The case", schema: AftersaleCaseView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const acceptAftersaleCounter = defineRoute({
    ...Party,
    permission: "aftersale:open",
    id: "aftersales.cases.accept-counter",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/accept-counter",
    marks: ["tenant", "idem"],
    summary: "The buyer accepts the seller's counter-offer, which goes on to its execution",
    prd: ["AFS-1"],
    request: { params: CaseId },
    responses: { 200: { description: "The case", schema: AftersaleCaseView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const shipAftersaleReturn = defineRoute({
    ...Party,
    permission: "aftersale:open",
    id: "aftersales.cases.return-shipment",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/return-shipment",
    marks: ["tenant", "idem"],
    summary: "The buyer records how the goods of an agreed return were sent back",
    prd: ["AFS-2"],
    request: {
        params: CaseId,
        body: z.object({
            carrier: z.string().trim().min(1).max(60),
            trackingNumber: z.string().trim().min(1).max(60),
            shippedAt: Timestamp,
        }),
    },
    responses: { 200: { description: "The case", schema: AftersaleCaseView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const confirmAftersaleReturn = defineRoute({
    ...Party,
    permission: "aftersale:respond",
    id: "aftersales.cases.confirm-return",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/confirm-return",
    marks: ["tenant", "idem"],
    summary: "The seller confirms the returned goods arrived; the refund is due within the RuleSet's response days",
    prd: ["AFS-2"],
    request: { params: CaseId },
    responses: { 200: { description: "The case", schema: AftersaleCaseView } },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const recordAftersaleSettlement = defineRoute({
    ...Party,
    permission: "aftersale:respond",
    id: "aftersales.cases.settlements",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/settlements",
    marks: ["tenant", "idem"],
    summary: "On an order paid by bank transfer, the seller records the agreed refund it paid the buyer (the agreed amount, the day, the bank's reference, and proof) or the replacement it sent; the buyer then confirms it arrived, or it is confirmed when the RuleSet's response days pass",
    prd: ["AFS-2"],
    request: {
        params: CaseId,
        body: z.discriminatedUnion("kind", [
            z.object({
                kind: z.literal("refund"),
                amount: Money,
                paidOn: z.iso.date(),
                reference: z.string().trim().min(1).max(100),
                proofFileId: Id.optional().describe("A clean `evidence` file of the order"),
            }),
            z.object({
                kind: z.literal("replacement"),
                carrier: z.string().trim().min(1).max(60),
                trackingNumber: z.string().trim().min(1).max(60),
                shippedAt: Timestamp,
            }),
        ]),
    },
    responses: { 201: { description: "The case", schema: AftersaleCaseView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
export const confirmAftersaleSettlement = defineRoute({
    ...Party,
    permission: "aftersale:open",
    id: "aftersales.cases.confirm-settlement",
    method: "POST",
    path: "/v1/aftersale-cases/{caseId}/confirm-settlement",
    marks: ["tenant", "idem"],
    summary: "The buyer confirms the recorded refund or replacement arrived, which closes the case; or says it did not, with a note, and the seller records it again",
    prd: ["AFS-2"],
    request: {
        params: CaseId,
        body: z.object({
            received: z.boolean(),
            note: Note.optional().describe("Required when it did not arrive"),
        }),
    },
    responses: { 200: { description: "The case", schema: AftersaleCaseView } },
    errors: [
        "AUTH_REQUIRED",
        "TENANT_REQUIRED",
        "PERMISSION_DENIED",
        "NOT_FOUND",
        "VALIDATION_FAILED",
        "INVALID_TRANSITION",
    ],
});
/** A case as its card in the order's conversation shows it (IM-3, card 088), read afresh for every viewer. */
export const AftersaleCaseCardData = z
    .object({
    orderId: Id,
    orderNumber: z.string(),
    type: AftersaleCaseType,
    status: AftersaleCaseStatus,
    requested: Money.nullable(),
    agreed: Money.nullable(),
    nextStep: AftersaleCaseStep,
    waitingOn: z.enum(["buyer", "seller", "platform"]).nullable(),
    responseDueAt: Timestamp.nullable(),
})
    .meta({ id: "AftersaleCaseCardData" });
export const aftersaleCaseRoutes = [
    openAftersaleCase,
    listAftersaleCases,
    countAftersaleCases,
    getAftersaleCase,
    addAftersaleEvidence,
    withdrawAftersaleCase,
    respondAftersaleCase,
    acceptAftersaleCounter,
    shipAftersaleReturn,
    confirmAftersaleReturn,
    recordAftersaleSettlement,
    confirmAftersaleSettlement,
    listAftersaleConversationMessages,
    escalateAftersaleCase,
    appealAftersaleDecision,
];
//# sourceMappingURL=case.routes.js.map